import {
    AccessResourceType,
    Event,
    EventAction,
    EventCategory,
    EventKind,
    EventPayload,
    LayerEntityType,
} from "@nova/schemas"
import { Permissions, permissionLabels } from "@nova/constants"

export type AnyEventPayload = Event["data"]

type NotificationVariableRequirements = {
    actorName: object
    projectName: { projectId: string }
    accessResourceName: { resourceType: AccessResourceType; resourceId: string }
    layerTypeLabel: { layerType: LayerEntityType }
    entityType: { resourceType: AccessResourceType }
    permissionLabel: { permission: number }
    commentSnippet: { commentId: string; projectId: string }
}

export type NotificationVariable = keyof NotificationVariableRequirements

type PayloadSupporting<V extends NotificationVariable> = Extract<
    Event,
    { data: NotificationVariableRequirements[V] }
>["data"]

export type VariablesSupportedBy<K extends EventKind> = {
    [V in NotificationVariable]: EventPayload<K> extends NotificationVariableRequirements[V]
        ? V
        : never
}[NotificationVariable]

export const layerTypeLabels: Record<LayerEntityType, string> = {
    orthoFlights: "Orthomosaic",
    infraredFlights: "Hotspot",
    liveVideoFlights: "Live stream",
    videoColorFinderFlights: "Colorfinder",
    imageColorFinderFlights: "Colorfinder",
    panoramaFlights: "Panorama",
    videoFlights: "Video",
    PDFRasters: "PDF",
    mergedFlights: "Merged flight",
    satelliteFlights: "Satellite",
    userLocationShares: "Location share",
}

export const categoryLabels: Record<EventCategory, string> = {
    [EventCategory.Layers]: "Layers",
    [EventCategory.Comments]: "Comments",
    [EventCategory.Access]: "Access",
    [EventCategory.Pdfs]: "PDFs",
    [EventCategory.PilotTracking]: "Pilot Tracking",
    [EventCategory.Atlas]: "Atlas",
}

export const actionLabels: Record<EventAction, string> = {
    [EventAction.Created]: "Created",
    [EventAction.Uploaded]: "Uploaded",
    [EventAction.Processed]: "Processed",
    [EventAction.ProcessingFailed]: "Processing failed",
    [EventAction.Exported]: "Exported",
    [EventAction.Generated]: "Generated",
    [EventAction.Deleted]: "Deleted",
    [EventAction.Archived]: "Archived",
    [EventAction.Unarchived]: "Restored",
    [EventAction.Started]: "Started",
    [EventAction.Ended]: "Ended",
    [EventAction.Shared]: "Shared",
    [EventAction.Requested]: "Requested",
    [EventAction.Granted]: "Granted",
    [EventAction.Removed]: "Removed",
    [EventAction.Failed]: "Failed",
}

const resourceTypeLabels: Record<AccessResourceType, string> = {
    workspace: "Workspace",
    entity: "Folder",
}

type VariableDefinition<V extends NotificationVariable> = {
    supportedBy: (data: AnyEventPayload) => data is PayloadSupporting<V>
    fallback: string
} & (
    | { source: "resolved" }
    | { source: "payload"; derive: (data: PayloadSupporting<V>) => string | undefined }
)

export const notificationVariableCatalog = {
    actorName: {
        source: "resolved",
        supportedBy: (_data): _data is PayloadSupporting<"actorName"> => true,
        fallback: "Someone",
    },
    projectName: {
        source: "resolved",
        supportedBy: (data): data is PayloadSupporting<"projectName"> => "projectId" in data,
        fallback: "a project",
    },
    accessResourceName: {
        source: "resolved",
        supportedBy: (data): data is PayloadSupporting<"accessResourceName"> =>
            "resourceType" in data && "resourceId" in data,
        fallback: "this",
    },
    layerTypeLabel: {
        source: "payload",
        supportedBy: (data): data is PayloadSupporting<"layerTypeLabel"> => "layerType" in data,
        derive: (data) => layerTypeLabels[data.layerType],
        fallback: "Layer",
    },
    entityType: {
        source: "payload",
        supportedBy: (data): data is PayloadSupporting<"entityType"> => "resourceType" in data,
        derive: (data) => resourceTypeLabels[data.resourceType],
        fallback: "",
    },
    permissionLabel: {
        source: "payload",
        supportedBy: (data): data is PayloadSupporting<"permissionLabel"> => "permission" in data,
        // oxlint-disable-next-line typescript/no-unnecessary-type-assertion
        derive: (data) => permissionLabels[data.permission as Permissions],
        fallback: "access",
    },
    commentSnippet: {
        source: "resolved",
        supportedBy: (data): data is PayloadSupporting<"commentSnippet"> =>
            "commentId" in data && "projectId" in data,
        fallback: "",
    },
} as const satisfies { [V in NotificationVariable]: VariableDefinition<V> }

export const notificationVariables = Object.keys(
    notificationVariableCatalog
) as NotificationVariable[]

export type ResolvedVariable = {
    [
        V in NotificationVariable
    ]: (typeof notificationVariableCatalog)[V]["source"] extends "resolved" ? V : never
}[NotificationVariable]

export type NotificationContext = Partial<Record<ResolvedVariable, string>>

export const resolvedVariables = notificationVariables.filter(
    (variable): variable is ResolvedVariable =>
        notificationVariableCatalog[variable].source === "resolved"
)

export const supportsVariable = Object.fromEntries(
    notificationVariables.map((variable) => [
        variable,
        notificationVariableCatalog[variable].supportedBy,
    ])
) as { [V in NotificationVariable]: (typeof notificationVariableCatalog)[V]["supportedBy"] }
